import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useLiff } from '@/hooks/useLiff'
import { apiClient, type User } from '@/lib/api'

export default function Home() {
  const { profile, accessToken, isReady, isLoggedIn, error } = useLiff()
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (isReady && accessToken) {
      apiClient.setAccessToken(accessToken)
      loadUser()
    }
  }, [isReady, accessToken])

  const loadUser = async () => {
    try {
      const response = await apiClient.getMe()
      setUser(response.user)
    } catch (error) {
      console.error('Failed to load user:', error)
    } finally {
      setLoading(false)
    }
  }

  if (!isReady || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cafe-cream to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafe-brown mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    )
  }

  if (error || !isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">エラーが発生しました</h1>
          <p className="text-gray-600 text-sm mb-4">
            {error || 'ログインが必要です'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-cafe-cream to-white">
      {/* ヘッダー */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-4 py-4">
          <div className="flex items-center space-x-3">
            <div className="text-3xl">☕</div>
            <div>
              <h1 className="text-lg font-bold text-cafe-brown">カフェスタッフ</h1>
              <p className="text-sm text-gray-600">
                こんにちは、{user?.display_name || profile?.displayName}さん
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* メインメニュー */}
      <main className="p-4 pb-20">
        <div className="space-y-4">
          {/* メニューグリッド */}
          <div className="grid grid-cols-2 gap-4">
            <Link 
              to="/attendance" 
              className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 active:scale-95 transition-transform"
            >
              <div className="text-center">
                <div className="text-4xl mb-3">⏰</div>
                <h3 className="font-bold text-gray-900 mb-1">出退勤</h3>
                <p className="text-xs text-gray-500">打刻・休憩管理</p>
              </div>
            </Link>

            <Link 
              to="/shift" 
              className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 active:scale-95 transition-transform"
            >
              <div className="text-center">
                <div className="text-4xl mb-3">📅</div>
                <h3 className="font-bold text-gray-900 mb-1">シフト</h3>
                <p className="text-xs text-gray-500">希望提出・確認</p>
              </div>
            </Link>

            <Link 
              to="/notices" 
              className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 active:scale-95 transition-transform"
            >
              <div className="text-center">
                <div className="text-4xl mb-3">📢</div>
                <h3 className="font-bold text-gray-900 mb-1">連絡板</h3>
                <p className="text-xs text-gray-500">お知らせ・連絡</p>
              </div>
            </Link>

            <Link 
              to="/salary" 
              className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 active:scale-95 transition-transform"
            >
              <div className="text-center">
                <div className="text-4xl mb-3">💰</div>
                <h3 className="font-bold text-gray-900 mb-1">給与</h3>
                <p className="text-xs text-gray-500">明細・履歴</p>
              </div>
            </Link>
          </div>

          {/* 管理者メニュー */}
          {user?.role === 'admin' && (
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg p-4">
              <h3 className="text-white font-bold mb-3 flex items-center">
                <span className="text-xl mr-2">👑</span>
                管理者メニュー
              </h3>
              <div className="grid grid-cols-3 gap-3">
                <Link 
                  to="/admin/shift" 
                  className="bg-white bg-opacity-20 backdrop-blur rounded-lg p-3 text-white text-center text-sm font-medium active:scale-95 transition-transform"
                >
                  シフト管理
                </Link>
                <Link 
                  to="/salary-admin" 
                  className="bg-white bg-opacity-20 backdrop-blur rounded-lg p-3 text-white text-center text-sm font-medium active:scale-95 transition-transform"
                >
                  給与管理
                </Link>
                <Link 
                  to="/staff-admin" 
                  className="bg-white bg-opacity-20 backdrop-blur rounded-lg p-3 text-white text-center text-sm font-medium active:scale-95 transition-transform"
                >
                  スタッフ管理
                </Link>
              </div>
            </div>
          )}

          {/* お知らせエリア */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
            <h3 className="font-bold text-gray-900 mb-3 flex items-center">
              <span className="text-xl mr-2">📢</span>
              最新のお知らせ
            </h3>
            <div className="space-y-2 text-sm">
              <div className="text-gray-600">
                • シフト希望は毎週金曜日までに提出してください
              </div>
              <div className="text-gray-600">
                • 出勤前に必ず打刻を忘れずに！
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}